// apps/office/src/pages/office/CartOverview.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  ShoppingCart,
  Eye,
  RefreshCw,
  Search,
  User,
  Mail,
  Phone,
  Trash2,
  DollarSign,
  Package,
  Clock,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';

// Constants
const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', color: 'bg-green-100 text-green-800' },
  { value: 'abandoned', label: 'Abandoned', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'converted', label: 'Converted', color: 'bg-blue-100 text-blue-800' },
  { value: 'expired', label: 'Expired', color: 'bg-red-100 text-red-800' },
];

const CartOverview = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCart, setSelectedCart] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [cartToDelete, setCartToDelete] = useState(null);

  // Fetch carts with items
  const { data: carts = [], isLoading, refetch, error } = useQuery({
    queryKey: ['cartOverview', user?.id],
    queryFn: async () => {
      try {
        // Fetch all carts
        const cartsData = await pb.collection('carts').getFullList({
          sort: '-updated',
          expand: 'user',
          filter: statusFilter !== 'all' ? `status = "${statusFilter}"` : '',
        });

        // Fetch cart items for each cart
        const cartDetails = await Promise.all(
          cartsData.map(async (cart) => {
            try {
              const items = await pb.collection('cart_items').getFullList({
                filter: `cartId = "${cart.id}"`,
              });
              
              const totalItems = items.reduce((sum, i) => sum + (i.quantity || 0), 0);
              const totalValue = items.reduce((sum, i) => sum + ((i.rate || 0) * (i.quantity || 0)), 0);
              
              return {
                ...cart,
                items,
                totalItems,
                totalValue,
                itemsCount: items.length,
              };
            } catch (err) {
              console.error(`Error fetching items for cart ${cart.id}:`, err);
              return {
                ...cart,
                items: [],
                totalItems: 0,
                totalValue: 0,
                itemsCount: 0,
              };
            }
          })
        );

        // Filter out carts with no items
        return cartDetails.filter(c => c.itemsCount > 0);
      } catch (error) {
        console.error('Error fetching carts:', error);
        toast.error('Failed to load cart data');
        return [];
      }
    },
    enabled: !!user,
    staleTime: 30000,
  });

  // Delete cart mutation
  const deleteCartMutation = useMutation({
    mutationFn: async (cartId) => {
      // First delete all cart items
      const items = await pb.collection('cart_items').getFullList({
        filter: `cartId = "${cartId}"`,
      });
      await Promise.all(items.map(item => pb.collection('cart_items').delete(item.id)));
      // Then delete the cart
      await pb.collection('carts').delete(cartId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['cartOverview']);
      toast.success('Cart deleted successfully');
      setIsDeleteConfirmOpen(false);
      setCartToDelete(null);
    },
    onError: (error) => {
      console.error('Delete error:', error);
      toast.error('Failed to delete cart: ' + (error.message || 'Unknown error'));
    },
  });

  // Update cart status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }) =>
      pb.collection('carts').update(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries(['cartOverview']);
      toast.success('Cart status updated');
    },
    onError: (error) => {
      console.error('Status update error:', error);
      toast.error('Failed to update status: ' + (error.message || 'Unknown error'));
    },
  });

  // Filter carts by search term
  const filteredCarts = carts.filter((cart) => {
    const clientName = cart.expand?.userId?.name || cart.expand?.userId?.email || '';
    return (
      clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cart.expand?.userId?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cart.expand?.userId?.phone?.includes(searchTerm)
    );
  });

  // Stats
  const stats = {
    total: carts.length,
    totalValue: carts.reduce((sum, c) => sum + c.totalValue, 0),
    totalItems: carts.reduce((sum, c) => sum + c.totalItems, 0),
    active: carts.filter(c => c.status === 'active').length,
    abandoned: carts.filter(c => c.status === 'abandoned').length,
  };

  // Handlers
  const handleViewClient = (userId) => {
    if (userId) {
      navigate(`/office/clients/${userId}`);
    } else {
      toast.info('No associated client found');
    }
  };

  const handleViewCartDetails = (cart) => {
    setSelectedCart(cart);
    setIsDetailOpen(true);
  };

  const handleDeleteCart = (cartId) => {
    setCartToDelete(cartId);
    setIsDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (cartToDelete) {
      deleteCartMutation.mutate(cartToDelete);
    }
  };

  const handleStatusChange = (cartId, newStatus) => {
    updateStatusMutation.mutate({ id: cartId, status: newStatus });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
    return <Badge className={option.color}>{option.label}</Badge>;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <Loader2 className="w-10 h-10 animate-spin text-indigo-500 mb-4" />
        <p className="text-slate-500">Loading carts...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-slate-700 font-medium">Failed to load cart data</p>
        <p className="text-slate-400 text-sm mt-1">{error.message}</p>
        <Button onClick={() => refetch()} className="mt-4">
          <RefreshCw className="w-4 h-4 mr-2" /> Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <ShoppingCart className="w-6 h-6 text-indigo-600" />
            Cart Overview
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Track active carts and client purchasing behavior
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          className="gap-2"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Total Carts</p>
            <p className="text-xl font-bold text-slate-800">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Active</p>
            <p className="text-xl font-bold text-green-600">{stats.active}</p>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Abandoned</p>
            <p className="text-xl font-bold text-yellow-600">{stats.abandoned}</p>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Total Items</p>
            <p className="text-xl font-bold text-slate-800">{stats.totalItems}</p>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Total Value</p>
            <p className="text-xl font-bold text-indigo-600">?{stats.totalValue.toLocaleString()}</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Table */}
      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <CardTitle className="text-base flex items-center gap-2">
              <Package className="w-4 h-4 text-indigo-500" />
              Client Carts
            </CardTitle>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name, email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 h-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-36 h-9">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredCarts.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No carts found</p>
              <p className="text-sm text-slate-400">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your filters' 
                  : 'Carts will appear here when clients add items'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead className="text-center">Items</TableHead>
                    <TableHead className="text-right">Total Qty</TableHead>
                    <TableHead className="text-right">Total Value</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Last Updated</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCarts.map((cart) => {
                    const client = cart.expand?.userId;
                    return (
                      <TableRow key={cart.id} className="hover:bg-slate-50">
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                              <User className="w-4 h-4 text-indigo-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-800">
                                {client?.name || client?.email || 'Guest User'}
                              </p>
                              {client?.name && (
                                <p className="text-xs text-slate-400 truncate max-w-[120px]">
                                  {client.email}
                                </p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-0.5">
                            {client?.email && (
                              <div className="flex items-center gap-1 text-xs">
                                <Mail className="w-3 h-3 text-slate-400" />
                                <span className="truncate max-w-[120px]">{client.email}</span>
                              </div>
                            )}
                            {client?.phone && (
                              <div className="flex items-center gap-1 text-xs">
                                <Phone className="w-3 h-3 text-slate-400" />
                                <span>{client.phone}</span>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">{cart.itemsCount}</TableCell>
                        <TableCell className="text-right">{cart.totalItems}</TableCell>
                        <TableCell className="text-right font-semibold text-indigo-600">
                          ?{cart.totalValue.toLocaleString()}
                        </TableCell>
                        <TableCell>{getStatusBadge(cart.status || 'active')}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1 text-xs text-slate-500">
                            <Clock className="w-3 h-3" />
                            {cart.updated 
                              ? format(new Date(cart.updated), 'dd MMM yyyy')
                              : 'N/A'}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewCartDetails(cart)}
                              className="h-8 w-8 p-0"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4 text-slate-400" />
                            </Button>
                            {client && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewClient(client.id)}
                                className="h-8 w-8 p-0"
                                title="View Client"
                              >
                                <User className="w-4 h-4 text-slate-400" />
                              </Button>
                            )}
                            <Select
                              value={cart.status || 'active'}
                              onValueChange={(val) => handleStatusChange(cart.id, val)}
                            >
                              <SelectTrigger className="w-[100px] h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {STATUS_OPTIONS.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteCart(cart.id)}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              title="Delete Cart"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cart Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-indigo-600" />
              Cart Details
            </DialogTitle>
            <DialogDescription>
              Items in {selectedCart?.expand?.userId?.name || 'Guest'}'s cart
            </DialogDescription>
          </DialogHeader>
          {selectedCart && (
            <div className="space-y-4">
              {/* Client Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">Client</p>
                  <p className="font-medium">{selectedCart.expand?.userId?.name || 'Guest'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Email</p>
                  <p className="text-sm">{selectedCart.expand?.userId?.email || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Status</p>
                  {getStatusBadge(selectedCart.status || 'active')}
                </div>
                <div>
                  <p className="text-xs text-slate-500">Total Value</p>
                  <p className="text-sm font-bold text-indigo-600">
                    ?{selectedCart.totalValue?.toLocaleString() || '0'}
                  </p>
                </div>
              </div>

              {/* Items Table */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-center">Qty</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedCart.items?.length > 0 ? (
                      selectedCart.items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.name || 'Unnamed Item'}</TableCell>
                          <TableCell className="text-right">?{item.rate?.toLocaleString() || '0'}</TableCell>
                          <TableCell className="text-center">{item.quantity || 0}</TableCell>
                          <TableCell className="text-right font-medium">
                            ?{((item.rate || 0) * (item.quantity || 0)).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-slate-400">
                          No items in cart
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setIsDetailOpen(false)}>
                  Close
                </Button>
                {selectedCart.expand?.userId && (
                  <Button
                    onClick={() => {
                      handleViewClient(selectedCart.expand.userId.id);
                      setIsDetailOpen(false);
                    }}
                    className="gap-2"
                  >
                    <User className="w-4 h-4" /> View Client
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this cart and all its items? 
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteCartMutation.isLoading}
              className="gap-2"
            >
              {deleteCartMutation.isLoading && (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <p className="text-xs text-slate-400 text-center border-t border-slate-200 pt-4">
        Carts are automatically updated when clients add or remove items
        <span className="block mt-1">Last updated: {format(new Date(), 'dd MMM yyyy, HH:mm')}</span>
      </p>
    </div>
  );
};

export default CartOverview;